import type { AddressDetails } from "#app/lib/hooks/use-map-infobox-data";
import type { MapPin, MapPinPropertyDetails } from "./map-types";

const numberFormatter = new Intl.NumberFormat();
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
/** One row of the property list. `headline` marks the rows a rep scans for
 *  first (size, age, value), so the list can weight them for the eye to find. */
export type PropertyDetailRow = {
  label: string;
  value: string;
  headline?: boolean;
};

export type PropertyDetailGroup = {
  title: string;
  rows: PropertyDetailRow[];
};

/** Anonymous row shapes for the property list while Melissa resolves — the
 *  scaffold must not NAME fields it cannot promise. */
export const PROPERTY_DETAIL_SKELETON_ROWS = [
  { labelWidth: 76, valueWidth: 64 },
  { labelWidth: 60, valueWidth: 42 },
  { labelWidth: 44, valueWidth: 96 },
  { labelWidth: 92, valueWidth: 56 },
] as const satisfies ReadonlyArray<{
  labelWidth: number;
  valueWidth: number;
}>;

export function resolvePropertyDetails(
  pin: MapPin,
  addressDetails: AddressDetails | null,
): MapPinPropertyDetails | null {
  return addressDetails?.propertyDetails ?? pin.propertyDetails ?? null;
}

/** Every property field Melissa answered with, grouped for the expanded list. */
export function propertyDetailGroups(
  details: MapPinPropertyDetails | null,
): PropertyDetailGroup[] {
  if (!details) return [];
  const structure: PropertyDetailRow[] = [];
  if (details.squareFootage) {
    structure.push({
      label: "Square feet",
      value: numberFormatter.format(details.squareFootage),
      headline: true,
    });
  }
  if (details.yearBuilt) {
    structure.push({
      label: "Year built",
      value: String(details.yearBuilt),
      headline: true,
    });
  }
  const owners = (details.ownerNames ?? []).filter(
    (name): name is string => Boolean(name),
  );
  const ownership: PropertyDetailRow[] = owners.length
    ? [{ label: "Owner", value: owners.join(" & ") }]
    : [];
  const value: PropertyDetailRow[] = details.marketValue
    ? [
        {
          label: "Est. market value",
          value: currencyFormatter.format(details.marketValue),
          headline: true,
        },
      ]
    : [];

  const groups: PropertyDetailGroup[] = [
    { title: "Structure", rows: structure },
    { title: "Ownership", rows: ownership },
    { title: "Value", rows: value },
  ];
  return groups.filter((group) => group.rows.length > 0);
}

/** The first value that holds text. A reverse geocode over open land answers
 *  with EMPTY strings, not nulls, so `??` alone lets a blank win over a
 *  populated fallback — every address field passes through here. */
function firstText(
  ...values: ReadonlyArray<string | null | undefined>
): string | null {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

/** City · state · zip on one line ("Boise, ID 83706"), or null. */
export function formatCityLine(
  pin: MapPin,
  addressDetails: AddressDetails | null,
): string | null {
  const joined = [
    firstText(addressDetails?.city, pin.city),
    firstText(addressDetails?.stateProvince, pin.stateProvince),
    firstText(addressDetails?.postalCode, pin.postalCode),
  ]
    .filter(Boolean)
    .join(", ")
    .replace(/, (\d)/, " $1");
  return joined || null;
}

/** Decimal degrees to four places (~11 m) — the precision a map pin has. */
const coordinateFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

/** "45.7031, -107.6120" — the place's only name when geocoding found none. */
export function formatCoordinateLine(pin: MapPin): string {
  return `${coordinateFormatter.format(pin.latitude)}, ${coordinateFormatter.format(pin.longitude)}`;
}

/** What a pin with no street is called. */
export const DROPPED_PIN_TITLE = "Dropped pin";

/** The infobox's identity block. Street first, then the whole formatted
 *  address; a pin over open land (no street, no town) is a "Dropped pin"
 *  named by its coordinates so the header is never blank. */
export function infoboxTitleBlock(
  pin: MapPin,
  addressDetails: AddressDetails | null,
): { title: string; subtitle: string | null } {
  const cityLine = formatCityLine(pin, addressDetails);
  const title = firstText(
    addressDetails?.addressLine1,
    pin.addressLine1,
    addressDetails?.wholeAddress,
    pin.wholeAddress,
  );
  if (title) return { title, subtitle: cityLine };
  return {
    title: DROPPED_PIN_TITLE,
    subtitle: cityLine ?? formatCoordinateLine(pin),
  };
}

/** The address a directions request navigates to, or null when the pin has
 *  no address text at all (a bare coordinate). */
export function directionsAddress(
  pin: MapPin,
  addressDetails: AddressDetails | null,
): string | null {
  const whole = firstText(addressDetails?.wholeAddress, pin.wholeAddress);
  if (whole) return whole;
  const line1 = firstText(addressDetails?.addressLine1, pin.addressLine1);
  const city = formatCityLine(pin, addressDetails);
  const joined = [line1, city].filter(Boolean).join(", ");
  return joined || null;
}
